import type { Component } from "vue";
import type { CommandItemBaseProps } from "./types";
import { useAxureCloudConfig } from "ls/state/useAxureCloudConfig";
import { computed } from "vue";
import { HighlightColors, TextColors } from "../colors/colors";
import CommandAiBlock from "./CommandAiBlock.vue";
import CommandAiButton from "./CommandAiButton.vue";
import CommandBlockquote from "./CommandBlockquote.vue";
import CommandBold from "./CommandBold.vue";
import CommandBulletedList from "./CommandBulletedList.vue";
import CommandCallout from "./CommandCallout.vue";
import CommandCodeBlock from "./CommandCodeBlock.vue";
import CommandColorHighlight from "./CommandColorHighlight.vue";
import CommandColorText from "./CommandColorText.vue";
import CommandCustomAiBlock from "./CommandCustomAiBlock.vue";
import CommandEmbed from "./CommandEmbed.vue";
import CommandEmoji from "./CommandEmoji.vue";
import CommandHeading from "./CommandHeading.vue";
import CommandHorizontalRule from "./CommandHorizontalRule.vue";
import CommandInlineCode from "./CommandInlineCode.vue";
import CommandItalic from "./CommandItalic.vue";
import CommandLink from "./CommandLink.vue";
import CommandMention from "./CommandMention.vue";
import CommandOrderedList from "./CommandOrderedList.vue";
import CommandRemoveFormatting from "./CommandRemoveFormatting.vue";
import CommandSection from "./CommandSection.vue";
import CommandStrikethrough from "./CommandStrikethrough.vue";
import CommandTable from "./CommandTable.vue";
import CommandTaskList from "./CommandTaskList.vue";
import CommandText from "./CommandText.vue";
import CommandUnderline from "./CommandUnderline.vue";

export interface Command<T extends Component = Component> {
  title: string;
  hidden?: boolean;
  aliases?: string[];
  component: T;
  whiteboardOnly?: boolean;
  props?: T extends new (...args: any) => any
    ? Omit<InstanceType<T>["$props"], keyof CommandItemBaseProps>
    : Record<string, any>;
}

// Factory function to enable Component-Props inference

export interface CommandGroup {
  name: string;
  alpha?: boolean;
  commands: Command[];
}

export function defineCommand<T extends Component>(command: Command<T>): Command<T> {
  return command;
}

const aiButtonsCommands: Command[] = [
  defineCommand({
    title: "Brainstorm options",
    component: CommandAiButton,
    props: {
      title: "Brainstorm options",
      aiAttributes: {
        text: "Brainstorm options",
        prompt: "Write a \"How might we\" statement based on the pain point the user provides. Then, generate a few creative and practical ideas that address this \"How Might We\" statement. Focus on solutions that are feasible, user-centered, and varied in approach (e.g., quick wins, innovative concepts, or long-term improvements).\n\nInput: [User's pain point]\n\nExample:\nInput: \"Remote team members aren't connecting or participating as much in meetings\"\nAI Output:\n\"How might we help remote teams feel more connected during meetings?\"\n- Add quick icebreaker prompts that rotate each meeting\n- Create a shared \"team mood board\" where members post wins or highlights\n- Introduce a rotating \"meeting host\" role to encourage participation\n- Use short breakout sessions for casual conversation or brainstorming",
        ask: "Let's brainstorm a few concepts from a **\"How might we...\"** statement based on user pain points.\n\nWhat pain point would you like to address? I'll write a \"How might we...\" statement and offer some suggestions?",
      },
    },
    aliases: ["aibrainstormoptions", "abrainstormoptions", "brainstormoptions"],
  }),
  defineCommand({
    title: "Business value",
    component: CommandAiButton,
    props: {
      title: "Business value",
      aiAttributes: {
        text: "Business value",
        prompt: "Write a value statement. Give the result in the following format, using bullets and markdown:\n\nBusiness impact:\n- Benefit or outcome\n- Benefit or outcome\n- Benefit or outcome",
        ask: "How does this help the business?",
      },
    },
    aliases: ["aibusinessvalue", "abusinessvalue", "businessvalue"],
  }),
  defineCommand({
    title: "Dependencies",
    component: CommandAiButton,
    props: {
      title: "Dependencies",
      aiAttributes: {
        text: "Dependencies",
        prompt: "Present the dependencies in bulleted format considering key resources, teams, or technologies needed",
        ask: "What are the dependencies?",
      },
    },
    aliases: ["aidependencies", "adependencies", "dependencies"],
  }),
  defineCommand({
    title: "Feature comparison",
    component: CommandAiButton,
    props: {
      title: "Feature comparison",
      aiAttributes: {
        text: "Feature comparison",
        prompt: "Summarize how it compares to an existing solution, its potential differentiation, and whether it supports the organization's direction or priorities.",
        ask: "Who else offers this and how does your solution compare to theirs?",
      },
    },
    aliases: ["aifeaturecomparison", "afeaturecomparison", "featurecomparison"],
  }),
  defineCommand({
    title: "Feature description",
    component: CommandAiButton,
    props: {
      title: "Feature description",
      aiAttributes: {
        text: "Feature description",
        prompt: "Write a concise description of this feature. Summarize what it does, who it's for, and the main value or benefit it provides to users.",
        ask: "Can you give me a brief description of the feature or product and tell me why you want to build it?",
      },
    },
    aliases: ["aifeaturedescription", "afeaturedescription", "featuredescription"],
  }),
  defineCommand({
    title: "Feasibility",
    component: CommandAiButton,
    props: {
      title: "Feasibility",
      aiAttributes: {
        text: "Feasibility",
        prompt: "Summarize the feasibility of this idea. Consider key dependencies, the estimated level of effort required, and potential risks or challenges. Highlight any factors that might affect how realistic or achievable the idea is.\n\nPresent this in bulleted format of 3 short sections:\nDependencies: key resources, teams, or technologies needed\nEffort: approximate scope or complexity\nRisks: main uncertainties or obstacles",
        ask: "What dependencies and risks should be considered? How much effort will be required to implement this feature?",
      },
    },
    aliases: ["aifeasibility", "afeasibility", "feasibility"],
  }),
  defineCommand({
    title: "Functional requirements",
    component: CommandAiButton,
    props: {
      title: "Functional requirements",
      aiAttributes: {
        text: "Functional requirements",
        prompt: "List the key functional requirements for this feature or idea in bullet format. Focus on what the system or product must do to meet user needs and support the intended outcomes.",
        ask: "What are the functional requirements for this feature?",
      },
    },
    aliases: ["aifunctionalrequirements", "afunctionalrequirements", "functionalrequirements"],
  }),
  defineCommand({
    title: "Goal",
    component: CommandAiButton,
    props: {
      title: "Goal",
      aiAttributes: {
        text: "Goal",
        prompt: "Write a clear goal statement for this idea or feature. Focus on what we want to achieve, who it benefits, and what success would look like.",
        ask: "What outcome would you like to achieve by solving this problem?",
      },
    },
    aliases: ["aigoal", "agoal", "goal"],
  }),
  defineCommand({
    title: "Idea",
    component: CommandAiButton,
    props: {
      title: "Idea",
      aiAttributes: {
        text: "Idea",
        prompt: "Summarize the idea in one sentence only: who it's for, what it does, why it matters\n\nExample:\nBuild a “Smart Notifications” feature that helps users stay informed without distraction in order to increase engagement and reduce churn.",
        ask: "What is the idea?",
      },
    },
    aliases: ["aiidea", "aidea", "idea"],
  }),
  defineCommand({
    title: "KPI",
    component: CommandAiButton,
    props: {
      title: "KPI",
      aiAttributes: {
        text: "KPI",
        prompt: "Based on this goal or initiative, suggest a clear KPI that measures success. Include the metric, how it will be tracked, and the target value or benchmark if possible.",
        ask: "Let's write a **KPI**.\n\nWhat are indicators that the feature successfully met the goal?",
      },
    },
    aliases: ["aikpi", "akpi", "kpi"],
  }),
  defineCommand({
    title: "Non-functional requirements",
    component: CommandAiButton,
    props: {
      title: "Non-functional requirements",
      aiAttributes: {
        text: "Non-functional requirements",
        prompt: "List the key non-functional requirements for this feature or system in bulleted format. Focus on how it should perform rather than what it does — for example, speed, reliability, security, scalability, and usability.",
        ask: "What are the non-functional requirements for this feature?",
      },
    },
    aliases: ["ainonfunctionalrequirements", "anonfunctionalrequirements", "nonfunctionalrequirements"],
  }),
  defineCommand({
    title: "Positioning statement",
    component: CommandAiButton,
    props: {
      title: "Positioning statement",
      aiAttributes: {
        text: "Positioning statement",
        prompt: "Write a two sentence product positioning statement in the form of: Problem statement. Product is a something that uniquely solves this problem.",
        ask: "Let's write a **positioning statement**.\n\nWho is the user, what problem do they have?\n\nWhat is your product and how does it solve this problem?",
      },
    },
    aliases: ["aipositioningstatement", "apositioningstatement", "positioningstatement"],
  }),
  defineCommand({
    title: "Problem statement",
    component: CommandAiButton,
    props: {
      title: "Problem statement",
      aiAttributes: {
        text: "Problem statement",
        prompt: "Write a clear, concise problem statement for this idea. Describe:\n- What the problem or pain point is\n- Who is experiencing the problem\n- Why it matters or what impact it has\n\nKeep it factual and empathetic — focus on the user's struggle, not the solution. ",
        ask: "Let's write a **problem statement**.\n\nWhat problem or pain point are you solving?",
      },
    },
    aliases: ["aiproblemstatement", "aproblemstatement", "problemstatement"],
  }),
  defineCommand({
    title: "Risks",
    component: CommandAiButton,
    props: {
      title: "Risks",
      aiAttributes: {
        text: "Risks",
        prompt: "Present the risks in bulleted format considering the main uncertainties or obstacles",
        ask: "What risks should be considered? ",
      },
    },
    aliases: ["airisks", "arisks", "risks"],
  }),
  defineCommand({
    title: "SMART goal",
    component: CommandAiButton,
    props: {
      title: "SMART goal",
      aiAttributes: {
        text: "SMART goal",
        prompt: "Based on the information provided, write a SMART Goal. Include the key elements needed for clarity and actionability. Make it:\n- Specific \n- Measurable \n- Achievable\n- Relevant\n- Time-bound",
        ask: "Let's write a  **SMART goal**.\n\nWhat is the goal? It should be Specific, Measurable, Achievable, Relevant, and Time-bound.",
      },
    },
    aliases: ["aismartgoal", "asmartgoal", "smartgoal"],
  }),
  defineCommand({
    title: "Strategic alignment",
    component: CommandAiButton,
    props: {
      title: "Strategic alignment",
      aiAttributes: {
        text: "Strategic alignment",
        prompt: "Summarize how this idea fits within the current market and aligns with our strategic goals. Consider whether it supports the organization's direction or priorities.",
        ask: "How does this align with company goals?",
      },
    },
    aliases: ["aistrategicalignment", "astrategicalignment", "strategicalignment"],
  }),
  defineCommand({
    title: "Target users",
    component: CommandAiButton,
    props: {
      title: "Target users",
      aiAttributes: {
        text: "Target users",
        prompt: "Based on this idea, identify the likely target users or audience segments. For each, describe:\n- Who they are (role, demographic, or context)\n- Their key needs or motivations\n- The pain points or goals this idea addresses for them\n\nKeep the list concise and insight-driven — focus on 2-3 clear user types.",
        ask: "Who are you trying to help?",
      },
    },
    aliases: ["aitargetusers", "atargetusers", "targetusers"],
  }),
  defineCommand({
    title: "User story",
    component: CommandAiButton,
    props: {
      title: "User story",
      aiAttributes: {
        text: "User story",
        prompt: "Write a user story in the following format including acceptance criteria:\nAs a [type of user], I want [goal or action] so that [benefit].\n- Given [initial condition]\n- When [action taken]\n- Then [expected outcome]",
        ask: "Let's write a **user story**.\n\nWho is the user, and what problem are they running into?\n\nWhat do they need to do that they can't do today?",
      },
    },
    aliases: ["aiuserstory", "auserstory", "userstory"],
  }),
  defineCommand({
    title: "User value",
    component: CommandAiButton,
    props: {
      title: "User value",
      aiAttributes: {
        text: "User value",
        prompt: "Write a value statement. Give the result in the following format, using bullets and markdown:\n\nUser impact:\n- Benefit or outcome\n- Benefit or outcome\n- Benefit or outcome",
        ask: "How does this help the user?",
      },
    },
    aliases: ["aiuservalue", "auservalue", "uservalue"],
  }),
];

export function useCommandGroups(isDoc: boolean) {
  const config = useAxureCloudConfig();

  const aiCommands: CommandGroup[] = config.value?.DisableAi
    ? []
    : [
        {
          name: "AI",
          alpha: true,
          commands: [
            {
              title: "AI chat",
              aliases: ["chat", "ai"],
              component: CommandAiBlock,
            },
            {
              title: "Browse AI snippets",
              aliases: ["snippets", "chat", "ai", "aibrowse", "abrowse"],
              component: CommandCustomAiBlock,
            },
            ...aiButtonsCommands.map(c => {
              return {
                ...c,
                hidden: true,
              };
            }),
          ],
        },
      ];

  const commands = computed(() => {
    const commandGroups: CommandGroup[] = [
      ...aiCommands,
      {
        name: "General",
        commands: [
          {
            title: "Text",
            aliases: ["paragraph"],
            component: CommandText,
          },
          defineCommand({
            title: "Large heading",
            aliases: ["h1", "one", "1", "#", "heading"],
            component: CommandHeading,
            props: { level: 1 },
          }),
          defineCommand({
            title: "Medium heading",
            aliases: ["h2", "two", "2", "##", "sub", "heading"],
            component: CommandHeading,
            props: { level: 2 },
          }),
          defineCommand({
            title: "Small heading",
            aliases: ["h3", "three", "3", "###", "subsub", "heading"],
            component: CommandHeading,
            props: { level: 3 },
          }),
          {
            title: "Bulleted list",
            aliases: ["list", "ordered", "*", "-"],
            component: CommandBulletedList,
          },
          {
            title: "Numbered list",
            aliases: ["list", "1."],
            component: CommandOrderedList,
          },
          {
            title: "Checklist",
            aliases: ["task", "list", "[", "]"],
            component: CommandTaskList,
          },
          {
            title: "Table",
            aliases: ["grid", "row", "cell"],
            component: CommandTable,
          },
          {
            title: "Divider",
            aliases: ["separator", "separate", "rule", "horizontal", "---", "___"],
            component: CommandHorizontalRule,
          },
          {
            title: "Code block",
            aliases: ["code", "```"],
            component: CommandCodeBlock,
          },
          {
            title: "Quote",
            aliases: ["blockquote", ">"],
            component: CommandBlockquote,
          },
          {
            title: "Link",
            aliases: ["link", "hyperlink"],
            component: CommandLink,
          },
          {
            title: "Section",
            aliases: ["section", "canvas"],
            component: CommandSection,
            whiteboardOnly: true,
          },
          {
            title: "Embed",
            aliases: ["embed", "link", "iframe", "smartlink"],
            component: CommandEmbed,
          },
          // {
          //   title: "AI Template",
          //   aliases: ["ai-template", "ai"],
          //   component: CommandAiBlock,
          // },
        ],
      },
      {
        name: "Communication",
        commands: [
          {
            title: "Emoji",
            aliases: [":"],
            component: CommandEmoji,
          },
          {
            title: "Callout",
            aliases: ["info"],
            component: CommandCallout,
          },
          {
            title: "Mention",
            aliases: ["@"],
            component: CommandMention,
          },
        ],
      },
      {
        name: "Color",
        commands: [
          defineCommand({
            title: "Default",
            component: CommandColorText,
            props: {
              color: ["Default", ""],
            },
          }),
        ].concat(Object.entries(TextColors).map(color => {
          const [name] = color;
          return {
            title: name,
            component: CommandColorText,
            props: { color },
          };
        })),
      },
      {
        name: "Background",
        commands: [
          defineCommand({
            title: "Default",
            component: CommandColorHighlight,
            props: { color: ["Default", ""] },
          }),
        ].concat(Object.entries(HighlightColors).map(color => {
          const [name] = color;
          return {
            title: name,
            component: CommandColorHighlight,
            props: { color },
          };
        })),
      },
      {
        name: "Text decoration",
        commands: [
          {
            title: "Bold",
            aliases: ["font", "weight"],
            component: CommandBold,
          },
          {
            title: "Italics",
            aliases: ["font"],
            component: CommandItalic,
          },
          {
            title: "Underline",
            aliases: ["font"],
            component: CommandUnderline,
          },
          {
            title: "Strikethrough",
            aliases: ["font"],
            component: CommandStrikethrough,
          },
          {
            title: "Inline code",
            aliases: ["font"],
            component: CommandInlineCode,
          },
          {
            title: "Remove formatting",
            aliases: ["clear", "font"],
            component: CommandRemoveFormatting,
          },
        ],
      },
    ] as const satisfies CommandGroup[];

    // make sure to update allowed content types in TableCell 'content' expression
    const AllowedTableBlockCommands: Component[] = [
      CommandText,
      CommandBulletedList,
      CommandOrderedList,
      CommandTaskList,
      CommandCodeBlock,
      CommandLink,
      CommandEmbed,
      CommandEmoji,
      CommandMention,
      CommandHorizontalRule,
    ] as const;

    const tableCommandGroups = commandGroups.map(group => {
      if (group.name === "General") {
        return {
          ...group,
          commands: group.commands.filter(c => !c.component || AllowedTableBlockCommands.includes(c.component)),
        };
      }

      return group;
    });

    // remove whiteboard only commands from general group when in document
    if (isDoc) {
      for (const group of commandGroups) {
        if (group.name === "General") {
          group.commands = group.commands.filter(c => !c.whiteboardOnly);
        }
      }
    }

    return {
      commandGroups,
      aiButtonsGroup: config.value?.DisableAi
        ? []
        : [
          {
            name: "AI",
            commands: aiButtonsCommands,
          },
        ] satisfies CommandGroup[],
      tableCommandGroups,
    };
  });

  return commands;
}
